import axios from 'axios';
import { loadConfig } from './config';

let apiUrl = '';

// Initialize API URL
loadConfig().then(config => {
  apiUrl = config.APIURL;
}).catch(() => {
  // config.json missing or invalid — apiUrl stays empty, handled at startup
});

// Convert base64 string to Uint8Array (required for VAPID key)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    });
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
}

// Subscribe to push notifications
export async function subscribeToPushNotifications() {
  try {
    // Ensure we have the API URL
    if (!apiUrl) {
      const config = await loadConfig();
      apiUrl = config.APIURL;
    }

    // Request permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    // Register service worker
    let registration = await navigator.serviceWorker.ready;
    if (!registration) {
      registration = await registerServiceWorker();
      await navigator.serviceWorker.ready;
    }

    // Get VAPID public key from server
    const { data: { publicKey } } = await axios.get(`${apiUrl}/push/public-key`, {
      headers: { accessToken: localStorage.getItem('accessToken') }
    });

    // Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    // Send subscription to server
    const subscriptionData = {
      endpoint: subscription.endpoint,
      expirationTime: subscription.expirationTime,
      keys: {
        p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
        auth: arrayBufferToBase64(subscription.getKey('auth'))
      }
    };

    await axios.post(`${apiUrl}/push/subscribe`, subscriptionData, {
      headers: { accessToken: localStorage.getItem('accessToken') }
    });

    console.log('Successfully subscribed to push notifications');
    return { success: true, subscription };
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return { success: false, error: error.message };
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPushNotifications() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();

      // Notify server
      await axios.post(`${apiUrl}/push/unsubscribe`, {
        endpoint: subscription.endpoint
      }, {
        headers: { accessToken: localStorage.getItem('accessToken') }
      });

      console.log('Successfully unsubscribed from push notifications');
      return { success: true };
    }

    return { success: true, message: 'No active subscription' };
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    return { success: false, error: error.message };
  }
}

// Check if currently subscribed
export async function isSubscribedToPush() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
}

// Get subscription status from server
export async function getSubscriptionStatus() {
  try {
    if (!apiUrl) {
      const config = await loadConfig();
      apiUrl = config.APIURL;
    }

    const { data } = await axios.get(`${apiUrl}/push/status`, {
      headers: { accessToken: localStorage.getItem('accessToken') }
    });
    return data;
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return { isSubscribed: false, subscriptions: [] };
  }
}

// Send test notification
export async function sendTestNotification() {
  try {
    if (!apiUrl) {
      const config = await loadConfig();
      apiUrl = config.APIURL;
    }

    const { data } = await axios.post(`${apiUrl}/push/test`, {}, {
      headers: { accessToken: localStorage.getItem('accessToken') }
    });
    return { success: true, message: data.success };
  } catch (error) {
    console.error('Error sending test notification:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
}

// Helper function to convert ArrayBuffer to base64
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

// Check if notifications are supported
export function areNotificationsSupported() {
  return 'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;
}

// Get current notification permission
export function getNotificationPermission() {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return 'denied';
}
